import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDividerModule } from '@angular/material/divider';
import { MatCardModule } from '@angular/material/card';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { StyleguideDialogComponent } from './styleguide-dialog/styleguide-dialog.component';
import { select, Store } from '@ngxs/store';
import { MainPaginationComponent } from '../../core/components/main-pagination/main-pagination.component';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { EditorJsonModel } from '../../editor/editor.json.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FetchOptionsState } from '../../core/state/fetchOptions.state';
import { AppSelectors } from '../../app.state';
import { EditorService } from '../../editor/editor.service';
import { getEditorRoleViewValue } from '../../editor/editor.role.enum';
import { DatePipe } from '@angular/common';
import { TableLoadingSpinnerComponent } from '../../utils/component/table-loading-spinner/table-loading-spinner';

@Component({
  selector: 'pixl-styleguide',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatSortModule,
    MatTableModule,
    MatButtonToggleModule,
    MatDividerModule,
    MatCardModule,
    MatTooltipModule,
    MainPaginationComponent,
    DatePipe,
    MatSortModule,
    TableLoadingSpinnerComponent
  ],
  templateUrl: './styleguide.component.html',
  styleUrl: './styleguide.component.scss'
})
export class StyleguideComponent implements OnInit {
  /**
   * This file isn't organized like normal. The code is split for each
   * block. Variables and important stuff are defined for each block in itself.
   * When you copy and paste any code from here please also
   * review the imports above and make sure you use the necessary ones.
   */

  /**
   * General logic
   */
  private readonly store = inject(Store);
  iconsVisible = signal(false);
  cardsVisible = signal(false);
  fieldsVisible = signal(false);
  buttonsVisible = signal(false);
  tableVisible = signal(false);
  tableSpinnerVisible = signal(false);
  dialogVisible = signal(false);

  /**
   * Logic for card
   */
  public cardHeadline = 'Karte';
  public cardBody = 'Lorem ipsum dolor sit amet consectetur adipisicing elit. Perspiciatis saepe minus consectetur, ratione porro earum nihil dolores quo.';

  /**
   * Logic for fields
   */
  // Combobox
  public comboBoxValues: string[] = ['Wert1', 'Wert2', 'Wert3', 'Wert4', 'Wert5'];
  private value = '';

  public onComboboxChangeEvent(event: string): void {
    // this is here just so something happens here :)
    this.value = event + 'hallo';
  }

  // Inputfield
  fb = inject(FormBuilder);
  inputFieldValue = '';

  userForm = this.fb.group({
    input: [this.inputFieldValue, [Validators.required]],
  });

  /**
   * Logic for buttons
   */
  public buttonclick() {
    // this is here just so something happens here :)
    this.value = this.value + 'click';
  }

  /**
   * Logic for table
   */
  @ViewChild(MatSort) sort!: MatSort;
  private _liveAnnouncer = inject(LiveAnnouncer);
  private editorService = inject(EditorService);
  private destroyRef = inject(DestroyRef);
  public displayedColumns: string[] = ['edit-actions', 'id', 'email', 'role', 'updated', 'array'];

  public dataSource = new MatTableDataSource<EditorJsonModel>([]);
  public appState = select(AppSelectors.appState);

  ngOnInit(): void {
    this.getListData();
  }

  getListData() {
    this.editorService.getAllWithPagination({}, {
      pageNumber: this.store.selectSnapshot(FetchOptionsState.getPageNumber),
      pageSize: this.store.selectSnapshot(FetchOptionsState.getPageSize)
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const [editors] = result;
          this.dataSource = new MatTableDataSource<EditorJsonModel>(editors);
          this.cdr.markForCheck();
        },
        error: () => {
          // is handled in requestInterceptor.ts
        },
      });
  }

  getArray(): string[] {
    return [
      'hallo',
      'halisdufslo',
      'hsefallo',
      'hasdfsllo',
      'hasdfsdallo',
      'haasdfjllo',
      'haerzerllo',
      'hwerwsvallo'
    ];
  }

  public announceSortChange(sortState: Sort) {
    this.sortData(sortState);
  }

  /**
   * This algorithm doesn't really work that well. But because it is used in most
   * if not all tables at the moment it is also displayed here. When it gets
   * fixed in general it also has to be fixed here
   */
  public sortData(sort: Sort): void {
    const data = this.dataSource.data.slice();

    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
    }
    else {
      this.dataSource.data = data.sort((a: EditorJsonModel, b: EditorJsonModel) => {
        const aValue = a[sort.active as keyof EditorJsonModel];
        const bValue = b[sort.active as keyof EditorJsonModel];
        return (aValue < bValue ? -1 : 1) * (sort.direction === 'asc' ? 1 : -1);
      });
    }
  }

  paginationChanged = (): void => {
    // refetch data
  };

  protected readonly getEditorRoleViewValue = getEditorRoleViewValue;

  /**
   * Logic for dialog
   */
  private dialog = inject(MatDialog);
  public cdr = inject(ChangeDetectorRef);

  openDialog(): void {
    this.dialog.open<StyleguideDialogComponent>(StyleguideDialogComponent, {
      minWidth: '40vw'
    });
  }
}
